import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import StaffPanelLayout from '../../components/staff/StaffPanelLayout';
import StaffHome from './StaffHome';
import StaffNotFound from './StaffNotFound';
import Features from './admin/Features';
import Mailbox from './Mailbox';

import AwardStamps from './general/AwardStamps';
import AwardAchievements from './general/AwardAchievements';
import AppreciationStaff from './general/Appreciation';

import ProfileReports from './moderation/ProfileReports';

import EventsSchedule from './events/Schedule';
import EventsStats from './events/Stats';

import ValuesOverview from './values/Overview';
import FurniDesk from './values/FurniDesk';
import ValueCategories from './values/Categories';

import ArticleEditor from './articles/ArticleEditor';
import MyArticles from './articles/MyArticles';
import ArticleManagement from './articles/ArticleManagement';
import ArticleCategories from './articles/ArticleCategories';
import NewObserver from './articles/NewObserver';

import MaintenanceAdmin from './admin/Maintenance';
import SessionsAdmin from './admin/Sessions';
import UsersAdmin from './admin/Users';
import PollsAdmin from './admin/Polls';
import ShopItems from './admin/ShopItems';
import ShopDiscounts from './admin/ShopDiscounts';
import ShopPurchases from './admin/ShopPurchases';
import BannerOffsets from './admin/BannerOffsets';
import ImageUpload from './admin/ImageUpload';
import ManageAchievements from './admin/ManageAchievements';
import RemoveAchievements from './admin/RemoveAchievements';
import ProfileSkinsAdmin from './admin/ProfileSkins';
import PfpBackgroundsAdmin from './admin/PfpBackgrounds';
import SpotlightArticles from './admin/SpotlightArticles';
import CloudUploadAdmin from './admin/CloudUploadAdmin';
import FranklyCloudPartners from './admin/FranklyCloudPartners';
import StaffTeamAdmin from './admin/StaffTeam';
import UserLookup from './admin/UserLookup';
import GdprDelete from './admin/GdprDelete';
import AskFrankOverview from './admin/askfrank/Overview';
import AskFrankSourcesDesk from './admin/askfrank/Sources';
import AskFrankKnowledge from './admin/askfrank/Knowledge';
import AskFrankFrontPage from './admin/askfrank/FrontPage';
import AskFrankLab from './admin/askfrank/Lab';
import SlimeSpinsAdmin from './admin/SlimeSpinsAdmin';
import SiteThemes from './admin/SiteThemes';

import FrankoPopAward from './frankopop/FrankoPopAward';
import FrankoPopSetsAdmin from './frankopop/FrankoPopSetsAdmin';
import FrankoPopSetEditor from './frankopop/FrankoPopSetEditor';
import FrankoPopRaritiesAdmin from './frankopop/FrankoPopRaritiesAdmin';
import FrankoPopAudit from './frankopop/FrankoPopAudit';

function ArticleEditorRoute(props) {
    const { articleId } = useParams();
    return <ArticleEditor key={articleId ?? 'new'} {...props} />;
}

function StaffPanel({ userData, furniItems }) {
    const isStaff = userData?.hasStaffRole === true;
    const isAdmin = userData?.hasAdminRole === true;

    useEffect(() => {
        document.title = isStaff ? 'Staff Panel - Frankly Origins' : 'Page Not Found';
    }, [isStaff]);

    if (!isStaff) {
        return <StaffNotFound />;
    }

    const adminOnly = (element) => (isAdmin ? element : <StaffNotFound inline />);

    return (
        <StaffPanelLayout userData={userData}>
            <Routes>
                <Route path="/staff" element={<StaffHome userData={userData} />} />
                <Route path="/staff/mailbox" element={<Mailbox userData={userData} />} />
                <Route path="/staff/mailbox/send" element={adminOnly(<Mailbox userData={userData} />)} />

                <Route path="/staff/general" element={<Navigate to="/staff/general/award-stamps" replace />} />
                <Route path="/staff/general/award-stamps" element={<AwardStamps userData={userData} />} />
                <Route path="/staff/general/award-achievements" element={<AwardAchievements userData={userData} />} />
                <Route path="/staff/general/appreciation" element={<AppreciationStaff userData={userData} />} />

                <Route path="/staff/frankopop" element={<Navigate to="/staff/frankopop/award" replace />} />
                <Route path="/staff/frankopop/award" element={<FrankoPopAward userData={userData} />} />
                <Route path="/staff/frankopop/sets" element={adminOnly(<FrankoPopSetsAdmin userData={userData} />)} />
                <Route path="/staff/frankopop/sets/:setId" element={adminOnly(<FrankoPopSetEditor userData={userData} />)} />
                <Route path="/staff/frankopop/rarities" element={adminOnly(<FrankoPopRaritiesAdmin userData={userData} />)} />
                <Route path="/staff/frankopop/audit" element={adminOnly(<FrankoPopAudit userData={userData} />)} />

                <Route path="/staff/moderation" element={<Navigate to="/staff/moderation/profile-reports" replace />} />
                <Route path="/staff/moderation/profile-reports" element={<ProfileReports userData={userData} />} />

                <Route path="/staff/events" element={<EventsSchedule userData={userData} />} />
                <Route path="/staff/events/stats" element={<EventsStats userData={userData} />} />

                <Route path="/staff/values" element={<ValuesOverview userData={userData} />} />
                <Route path="/staff/values/furni" element={<FurniDesk userData={userData} />} />
                <Route path="/staff/values/categories" element={<ValueCategories userData={userData} />} />
                <Route path="/staff/values/furni-editor" element={<Navigate to="/staff/values/furni" replace />} />
                <Route path="/staff/values/update-value" element={<Navigate to="/staff/values/furni" replace />} />
                <Route path="/staff/values/add-category" element={<Navigate to="/staff/values/categories" replace />} />

                <Route path="/staff/articles" element={<Navigate to="/staff/articles/new" replace />} />
                <Route path="/staff/articles/new" element={<ArticleEditorRoute userData={userData} />} />
                <Route path="/staff/articles/my-articles" element={<MyArticles userData={userData} />} />
                <Route path="/staff/articles/edit/:articleId" element={<ArticleEditorRoute userData={userData} />} />
                <Route path="/staff/articles/new-observer" element={<NewObserver userData={userData} />} />
                <Route path="/staff/articles/management" element={adminOnly(<ArticleManagement userData={userData} />)} />
                <Route path="/staff/articles/categories" element={adminOnly(<ArticleCategories userData={userData} />)} />

                <Route path="/staff/admin" element={<Navigate to="/staff" replace />} />
                <Route path="/staff/admin/maintenance" element={adminOnly(<MaintenanceAdmin userData={userData} />)} />
                <Route path="/staff/admin/features" element={adminOnly(<Features userData={userData} />)} />
                <Route path="/staff/admin/sessions" element={adminOnly(<SessionsAdmin userData={userData} />)} />
                <Route path="/staff/admin/users" element={adminOnly(<UsersAdmin userData={userData} />)} />
                <Route path="/staff/admin/polls" element={adminOnly(<PollsAdmin userData={userData} />)} />
                <Route path="/staff/admin/shop-items" element={adminOnly(<ShopItems userData={userData} />)} />
                <Route path="/staff/admin/shop-discounts" element={adminOnly(<ShopDiscounts userData={userData} />)} />
                <Route path="/staff/admin/shop-purchases" element={adminOnly(<ShopPurchases userData={userData} />)} />
                <Route path="/staff/admin/banner-offsets" element={adminOnly(<BannerOffsets userData={userData} furniItems={furniItems} />)} />
                <Route path="/staff/admin/imageupload" element={adminOnly(<ImageUpload userData={userData} />)} />
                <Route path="/staff/admin/themes" element={adminOnly(<SiteThemes userData={userData} />)} />
                <Route path="/staff/admin/manage-achievements" element={adminOnly(<ManageAchievements userData={userData} />)} />
                <Route path="/staff/admin/remove-achievements" element={adminOnly(<RemoveAchievements userData={userData} />)} />
                <Route path="/staff/admin/skins" element={adminOnly(<ProfileSkinsAdmin userData={userData} />)} />
                <Route path="/staff/admin/pfpbgs" element={adminOnly(<PfpBackgroundsAdmin userData={userData} />)} />
                <Route path="/staff/admin/spotlight-articles" element={adminOnly(<SpotlightArticles userData={userData} />)} />
                <Route path="/staff/admin/cloud-upload" element={adminOnly(<CloudUploadAdmin userData={userData} />)} />
                <Route path="/staff/admin/cloud-partners" element={adminOnly(<FranklyCloudPartners userData={userData} />)} />
                <Route path="/staff/admin/staff-team" element={adminOnly(<StaffTeamAdmin userData={userData} />)} />
                <Route path="/staff/admin/user-lookup" element={adminOnly(<UserLookup userData={userData} />)} />
                <Route path="/staff/admin/gdpr-delete" element={adminOnly(<GdprDelete userData={userData} />)} />
                <Route path="/staff/admin/askfrank" element={adminOnly(<AskFrankOverview userData={userData} />)} />
                <Route path="/staff/admin/askfrank/sources" element={adminOnly(<AskFrankSourcesDesk userData={userData} />)} />
                <Route path="/staff/admin/askfrank/knowledge" element={adminOnly(<AskFrankKnowledge userData={userData} />)} />
                <Route path="/staff/admin/askfrank/frontpage" element={adminOnly(<AskFrankFrontPage userData={userData} />)} />
                <Route path="/staff/admin/askfrank/words" element={<Navigate to="/staff/admin/askfrank/knowledge" replace />} />
                <Route path="/staff/admin/askfrank/voice" element={<Navigate to="/staff/admin/askfrank/frontpage" replace />} />
                <Route path="/staff/admin/askfrank/lab" element={adminOnly(<AskFrankLab userData={userData} />)} />
                <Route path="/staff/admin/askfrank-sources" element={<Navigate to="/staff/admin/askfrank/sources" replace />} />
                <Route path="/staff/admin/askfrank-net" element={<Navigate to="/staff/admin/askfrank/frontpage" replace />} />
                <Route path="/staff/admin/askfrank-entities" element={<Navigate to="/staff/admin/askfrank/knowledge" replace />} />
                <Route path="/staff/admin/askfrankbrain" element={<Navigate to="/staff/admin/askfrank/lab" replace />} />
                <Route path="/staff/admin/slimespins" element={adminOnly(<SlimeSpinsAdmin userData={userData} />)} />
                <Route path="/staff/admin/articles/edit/:articleId" element={adminOnly(<ArticleEditorRoute userData={userData} adminMode={true} />)} />

                <Route path="*" element={<StaffNotFound inline />} />
            </Routes>
        </StaffPanelLayout>
    );
}

export default StaffPanel;
